import { FormEvent, useCallback, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useEffectOnce } from 'react-use';
import { BusinessGeoDto } from '../../../data-layer/business/types';
import { loadGeoSuggestsAsync } from '../../../data-layer/geo/actions';
import { selectGeo } from '../../../data-layer/geo/selectors';
import { useDispatch } from '../../../data-layer/useDispatch';
import { useSelector } from '../../../data-layer/useSelector';

const DEFAULT_GEO_NAME = 'Россия';

const DEFAULT_GEO = { '225': DEFAULT_GEO_NAME };

const TIMEOUT = 200;

function getRegionsSettings(regions?: BusinessGeoDto[]) {
    if (!regions?.length) {
        return DEFAULT_GEO;
    }

    return regions.reduce<Record<string, string>>(
        (res, region) => ({
            ...res,
            [String(region.region_id)]: region.name,
        }),
        {},
    );
}

export const useBusinessGeo = (regions?: BusinessGeoDto[]) => {
    const dispatch = useDispatch();
    const [geoChanged, setGeoChanged] = useState(false);
    const geo = useSelector((state) => selectGeo(state));
    const [regionQuery, setRegionQuery] = useState('');
    const onLoadGeoSuggests = useMemo(
        () =>
            debounce((query?: string) => {
                dispatch(loadGeoSuggestsAsync(query));
            }, TIMEOUT),
        [dispatch],
    );

    const [selectedRegions, setSelectedRegions] = useState<Record<string, string>>(
        getRegionsSettings(regions),
    );

    const onSelectRegion = useCallback(
        (value: Record<string, string>) => {
            setSelectedRegions({ ...selectedRegions, ...value });
            setRegionQuery('');
            setGeoChanged(true);
        },
        [selectedRegions],
    );

    const onRemoveRegion = useCallback(
        (key: string) => {
            const newSelectedRegions = { ...selectedRegions };

            delete newSelectedRegions[key];

            setSelectedRegions(newSelectedRegions);
            setGeoChanged(true);
        },
        [selectedRegions],
    );

    const onInputRegion = useCallback(
        (event: FormEvent<HTMLInputElement>) => {
            const value = event.currentTarget.value;

            setRegionQuery(value);
            onLoadGeoSuggests(value);
        },
        [onLoadGeoSuggests],
    );

    const updateSelectedRegions = useCallback((newRegions?: BusinessGeoDto[]) => {
        setSelectedRegions(getRegionsSettings(newRegions));
    }, []);

    useEffectOnce(() => {
        onLoadGeoSuggests(DEFAULT_GEO_NAME);
    });

    return {
        selectedRegions,
        geoSuggests: geo.suggests,
        regionQuery,
        geoChanged,
        onSelectRegion,
        onRemoveRegion,
        onInputRegion,
        updateSelectedRegions,
    };
};
